import {
  BiChartType,
  BiChartViz,
  BiLineWidth,
  BiVizStyle,
  BiVizType,
} from '@euler/model/bi/viz';
import {
  BiGalleryCategory,
  BiGalleryCategoryKey,
  BiGalleryDashboardCard,
  BiGalleryVariant,
} from '../functions/biGalleryTypes';
import {
  cents,
  kGalleryFields as f,
  galleryReady,
  galleryResult,
  kGalleryMonths,
  kGalleryServiceTypes,
} from './biGalleryFields';

const revenue = [
  182400, 168900, 205300, 221800, 238600, 226100, 251400, 263900, 248700,
  272300,
];
const newCustomers = [86, 74, 95, 102, 110, 98, 121, 127, 115, 132];
const returning = [212, 198, 236, 251, 268, 259, 284, 296, 281, 305];
const byType: Record<string, number[]> = {
  常规保养: [
    98200, 91500, 108400, 116900, 124300, 119800, 131200, 138600, 129900,
    142700,
  ],
  钣金喷漆: [
    52100, 47800, 61300, 66200, 71900, 64400, 75100, 78300, 74200, 81600,
  ],
  轮胎更换: [
    32100, 29600, 35600, 38700, 42400, 41900, 45100, 47000, 44600, 48000,
  ],
};
const dailyOrders = [32, 28, 35, null, null, 41, 38, 36, 30, null, 44, 47];

const revenueResult = galleryResult(
  [f.month, f.revenue],
  kGalleryMonths.map((month, i) => [month, cents(revenue[i])]),
);
const customersResult = galleryResult(
  [f.month, f.newCustomers, f.returning],
  kGalleryMonths.map((month, i) => [month, newCustomers[i], returning[i]]),
);
const byTypeResult = galleryResult(
  [f.month, f.serviceType, f.revenue],
  kGalleryMonths.flatMap((month, i) =>
    kGalleryServiceTypes.map(type => [month, type, cents(byType[type][i])]),
  ),
);
const gapsResult = galleryResult(
  [f.day, f.orders],
  dailyOrders.map((value, i) => [
    `2026-10-${String(i + 1).padStart(2, '0')}`,
    value,
  ]),
);

function revenueCard(
  title: string,
  style?: BiVizStyle,
  type: BiChartType = BiVizType.Line,
): BiGalleryDashboardCard {
  const viz: BiChartViz = {
    type,
    x: f.month.key,
    series: [{ key: f.revenue.key }],
    ...(style ? { style } : {}),
  };
  return {
    surface: 'dashboard',
    title,
    viz,
    data: galleryReady(revenueResult),
  };
}

function customersCard(
  title: string,
  type: BiVizType.Line | BiVizType.Area,
): BiGalleryDashboardCard {
  return {
    surface: 'dashboard',
    title,
    viz: {
      type,
      x: f.month.key,
      series: [{ key: f.newCustomers.key }, { key: f.returning.key }],
    },
    data: galleryReady(customersResult),
  };
}

function widthVariant(width: BiLineWidth): BiGalleryVariant {
  return {
    key: `width-${width}`,
    name: `线宽 ${width}`,
    note: `line.width = ${width}`,
    card: revenueCard('月度营收', {
      line: { width, smooth: false, showPoints: true },
    }),
  };
}

const single = revenueCard('月度营收');

export const kLineCategory: BiGalleryCategory = {
  key: BiGalleryCategoryKey.Line,
  name: '折线图 / 面积图',
  description: '趋势：单条、多条、按维度拆分、平滑、线宽、数据点、标签、空值',
  preview: single,
  variants: [
    { key: 'single', name: '单条折线', card: single },
    {
      key: 'multi',
      name: '多条折线',
      note: '两个指标，图例默认显示',
      card: customersCard('新老客户数', BiVizType.Line),
    },
    {
      key: 'series-by',
      name: '按维度拆分',
      note: 'seriesBy = 服务类型',
      card: {
        surface: 'dashboard',
        title: '各服务类型月度营收',
        viz: {
          type: BiVizType.Line,
          x: f.month.key,
          series: [{ key: f.revenue.key }],
          seriesBy: f.serviceType.key,
        },
        data: galleryReady(byTypeResult),
      },
    },
    {
      key: 'smooth',
      name: '平滑曲线',
      note: 'line.smooth = true',
      card: revenueCard('月度营收', {
        line: { width: 2, smooth: true, showPoints: true },
      }),
    },
    {
      key: 'straight',
      name: '直线',
      note: 'line.smooth = false',
      card: revenueCard('月度营收', {
        line: { width: 2, smooth: false, showPoints: true },
      }),
    },
    widthVariant(1),
    widthVariant(2),
    widthVariant(3),
    {
      key: 'no-points',
      name: '隐藏数据点',
      note: 'line.showPoints = false',
      card: revenueCard('月度营收', {
        line: { width: 2, smooth: true, showPoints: false },
      }),
    },
    {
      key: 'labels',
      name: '数据标签',
      note: 'dataLabels = true',
      card: revenueCard('月度营收', { dataLabels: true }),
    },
    {
      key: 'gaps',
      name: '空值断点',
      note: '4、5、10 日无数据：断开，不画成 0',
      card: {
        surface: 'dashboard',
        title: '本月每日工单数',
        viz: {
          type: BiVizType.Line,
          x: f.day.key,
          series: [{ key: f.orders.key }],
        },
        data: galleryReady(gapsResult),
      },
    },
    {
      key: 'area',
      name: '面积图',
      card: revenueCard('月度营收', undefined, BiVizType.Area),
    },
    {
      key: 'area-multi',
      name: '面积图 · 多系列',
      card: customersCard('新老客户数', BiVizType.Area),
    },
  ],
};
